import { AttractionsList } from '@/components/attractions';
import { HotelsList } from '@/components/hotels';
import { NeighborhoodsList } from '@/components/neighborhoods';
import { ToursList } from '@/components/tours';
import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import { greaterAreaCrudConfig, type GreaterArea } from '@/config/objects/greater-areas';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { neighborhoodCrudConfig, type Neighborhood } from '@/config/objects/neighborhoods';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Greater Area detail page
 */
export function GreaterAreaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [greaterArea, setGreaterArea] = useState<GreaterArea | null>(null);

  /**
   * Delete greater area mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (greaterAreaId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/greater-areas/${greaterAreaId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate greater areas list to refresh data
        queryClient.invalidateQueries({ queryKey: ['GreaterArea'] });
        navigate('/greater-areas');
      } else {
        throw new Error(response.error || 'Failed to delete greater area');
      }
    },
  });

  // Create custom config for greater area-specific neighborhoods
  const greaterAreaNeighborhoodsConfig: CrudConfig<Neighborhood> = useMemo(
    () => ({
      ...neighborhoodCrudConfig,
      entityName: 'Neighborhood',
      entityNamePlural: 'Neighborhoods',
      endpoints: {
        ...neighborhoodCrudConfig.endpoints,
        list: `/greater-areas/${id}/neighborhoods`,
        delete: (neighborhoodId: string) => `/greater-areas/${id}/neighborhoods/${neighborhoodId}`,
      },
      listView: {
        ...neighborhoodCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/neighborhoods',
            where: {
              greaterAreaId: {
                not: id,
              },
            },
            onAdd: async (neighborhood: Neighborhood) => {
              await apiClient.post(`/greater-areas/${id}/neighborhoods`, {
                neighborhoodId: neighborhood.id,
              });
              toast.success('Neighborhood added to greater area');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (neighborhood: Neighborhood) => {
              await apiClient.delete(`/greater-areas/${id}/neighborhoods/${neighborhood.id}`);
              toast.success('Neighborhood removed from greater area');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  // Create custom config for greater area-specific hotels
  const greaterAreaHotelsConfig: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/greater-areas/${id}/hotels`,
        delete: (hotelId: string) => `/greater-areas/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            where: {
              greaterAreaId: {
                not: id,
              },
            },
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/greater-areas/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to greater area');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/greater-areas/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from greater area');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  // Create custom config for greater area-specific tours
  const greaterAreaToursConfig: CrudConfig<Tour> = useMemo(
    () => ({
      ...tourCrudConfig,
      entityName: 'Tour',
      entityNamePlural: 'Tours',
      endpoints: {
        ...tourCrudConfig.endpoints,
        list: `/greater-areas/${id}/tours`,
        delete: (tourId: string) => `/greater-areas/${id}/tours/${tourId}`,
      },
      listView: {
        ...tourCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/tours',
            where: {
              greaterAreaId: {
                not: id,
              },
            },
            onAdd: async (tour: Tour) => {
              await apiClient.post(`/greater-areas/${id}/tours`, { tourId: tour.id });
              toast.success('Tour added to greater area');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (tour: Tour) => {
              await apiClient.delete(`/greater-areas/${id}/tours/${tour.id}`);
              toast.success('Tour removed from greater area');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  // Create custom config for greater area-specific attractions
  const greaterAreaAttractionsConfig: CrudConfig<Attraction> = useMemo(
    () => ({
      ...attractionCrudConfig,
      entityName: 'Attraction',
      entityNamePlural: 'Attractions',
      endpoints: {
        ...attractionCrudConfig.endpoints,
        list: `/greater-areas/${id}/attractions`,
        delete: (attractionId: string) => `/greater-areas/${id}/attractions/${attractionId}`,
      },
      listView: {
        ...attractionCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attractions',
            where: {
              greaterAreaId: {
                not: id,
              },
            },
            onAdd: async (attraction: Attraction) => {
              await apiClient.post(`/greater-areas/${id}/attractions`, {
                attractionId: attraction.id,
              });
              toast.success('Attraction added to greater area');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attraction: Attraction) => {
              await apiClient.delete(`/greater-areas/${id}/attractions/${attraction.id}`);
              toast.success('Attraction removed from greater area');
            },
          },
        },
      },
    }),
    [id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Greater Area Not Found</h2>
          <p className="text-muted-foreground">The requested greater area could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this greater area
   */
  const handleEdit = (greaterArea: GreaterArea) => {
    navigate(`/greater-areas/${greaterArea.id}/edit`);
  };

  /**
   * Handle greater area deletion
   */
  const handleDelete = async (greaterArea: GreaterArea) => {
    return toast.promise(deleteMutation.mutateAsync(greaterArea.id), {
      loading: 'Deleting greater area...',
      success: 'Greater area deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete greater area',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Greater Areas', href: '/greater-areas' },
    { label: greaterArea?.name || 'Greater Area', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={greaterAreaCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(greaterAreaData: GreaterArea) => setGreaterArea(greaterAreaData)}
        />

        {/* Neighborhoods Section */}
        <NeighborhoodsList
          config={greaterAreaNeighborhoodsConfig}
          className="p-0"
          baseUrl="/neighborhoods"
        />

        {/* Hotels Section */}
        <HotelsList config={greaterAreaHotelsConfig} className="p-0" baseUrl="/hotels" />

        {/* Tours Section */}
        <ToursList config={greaterAreaToursConfig} className="p-0" baseUrl="/tours" />

        {/* Attractions Section */}
        <AttractionsList
          config={greaterAreaAttractionsConfig}
          className="p-0"
          baseUrl="/attractions"
        />
      </div>
    </PageContainer>
  );
}
